/**
 * Types and utilities for maintaining a set of entities.
 */
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { EntitySet } from "building-types/entities/entitySetTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RWTable } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import objectIsEmpty from "common/utils/objectIsEmpty";
import objectKeyCount from "common/utils/objectKeyCount";
import type { Mutable } from "common/utils/types";
import { docEntityHandleHash } from "./core/handleHash";

export const emptyEntitySet = Object.freeze({});

const alwaysTrue = () => true;

export function unselectEntitiesByType(selected: EntitySet, type: DocEntityHandle["type"]) {
  let removed = false;
  const newSelected = { ...selected };
  for (const entityPath in selected) {
    const entity = selected[entityPath];
    if (entity.type === type) {
      delete newSelected[entityPath];
      removed = true;
    }
  }
  return removed ? newSelected : selected;
}

export function selectedEntitiesByPredicate(selected: EntitySet, predicate: (entity: DocEntityHandle) => boolean) {
  const selectedEntities: RWTable<DocEntityHandle> = {};

  for (const entityPath in selected) {
    const entity = selected[entityPath];
    if (predicate(entity)) {
      selectedEntities[entityPath] = entity;
    }
  }
  return selectedEntities;
}

export function selectedEntitiesByType<T extends DocEntityType>(
  selected: EntitySet,
  type: T | T[],
  predicate: (handle: Extract<DocEntityHandle, { type: T }>) => boolean = alwaysTrue,
): EntitySet<Extract<DocEntityHandle, { type: T }>> {
  const selectedEntities: Mutable<EntitySet> = {};

  if (!Array.isArray(type)) {
    for (const entityPath in selected) {
      const entity = selected[entityPath] as Extract<DocEntityHandle, { type: T }>;
      if (entity.type === type && predicate(entity)) {
        selectedEntities[entityPath] = entity;
      }
    }
    return selectedEntities as EntitySet<Extract<DocEntityHandle, { type: T }>>;
  }

  const typeLookup = type.length > 3 ? makeEntityTypeLookup(type) : null;

  for (const entityPath in selected) {
    const entity = selected[entityPath] as Extract<DocEntityHandle, { type: T }>;
    const matchesType = typeLookup ? !!typeLookup[entity.type] : type.includes(entity.type as T);
    if (matchesType && predicate(entity)) {
      selectedEntities[entityPath] = entity;
    }
  }
  return selectedEntities as EntitySet<Extract<DocEntityHandle, { type: T }>>;
}

export function singleSelectedEntityOfType<T extends DocEntityHandle["type"]>(
  selected: EntitySet,
  type: T,
): Extract<DocEntityHandle, { type: T }> | null {
  for (const entityPath in selected) {
    const entity = selected[entityPath];
    if (entity.type === type) {
      return entity as Extract<DocEntityHandle, { type: T }>;
    }
  }
  return null;
}

export function entitySetNew<T extends DocEntityHandle>(handles: T | T[]): EntitySet {
  const setHandles = Array.isArray(handles) ? handles : [handles];

  if (setHandles.length === 0) {
    return emptyEntitySet;
  }

  const set = {};
  for (const handle of setHandles) {
    if (handle) {
      set[docEntityHandleHash(handle)] = handle;
    }
  }
  return set;
}

export type SetOp = "SET" | "ADD" | "XOR" | "DIFF";

export function entitySetApplyOp(a: EntitySet, b: EntitySet, op: SetOp) {
  switch (op) {
    case "SET":
      return b;
    case "ADD":
      return entitySetUnion(a, b);
    case "XOR":
      return entitySetXOR(a, b);
    case "DIFF":
      return entitySetDiff(a, b);
    default:
      assertUnreachable(op);
  }
}

export function entitySetContainsMultipleTypes(
  set: EntitySet,
  compareSubTypePredicate: (firstHandle: DocEntityHandle, handle: DocEntityHandle) => boolean = alwaysTrue,
) {
  let firstHandle: DocEntityHandle | undefined;

  for (const hash in set) {
    const handle = set[hash];

    if (firstHandle === undefined) {
      firstHandle = handle;
    } else if (handle.type !== firstHandle.type) {
      return true;
    } else if (!compareSubTypePredicate(firstHandle, handle)) {
      return true;
    }
  }

  return false;
}

export function entitySetEqual(a: EntitySet, b: EntitySet) {
  if (a === b) {
    return true;
  }

  if (objectKeyCount(a) !== objectKeyCount(b)) {
    return false;
  }

  for (const hash in a) {
    if (!b[hash]) {
      return false;
    }
  }

  return true;
}

/**
 * True when `set` contains exactly the same handles as `handles` (by hash), without allocating a new EntitySet.
 * Duplicate / falsy entries in `handles` are ignored, matching `entitySetNew` semantics.
 */
export function entitySetEqualsHandles(
  set: EntitySet,
  handles: readonly (DocEntityHandle | null | undefined)[],
): boolean {
  let nonNullCount = 0;
  for (const handle of handles) {
    if (!handle) {
      continue;
    }
    nonNullCount++;
    if (!set[docEntityHandleHash(handle)]) {
      return false;
    }
  }

  const size = objectKeyCount(set);
  if (nonNullCount < size) {
    return false;
  }

  // size 0: membership check already required nonNullCount === 0.
  // size 1: every surviving handle must hash to that single member, so unique count is 1.
  if (size <= 1) {
    return true;
  }

  // With 2+ members, duplicates in `handles` can make nonNullCount === size while unique
  // membership is still a proper subset — always compare unique hashes.
  const seen = new Set<string>();
  for (const handle of handles) {
    if (!handle) {
      continue;
    }
    seen.add(docEntityHandleHash(handle));
  }
  return seen.size === size;
}

/**
 * True when every handle in `a` is also present in `b`.
 */
export function entitySetIsSubset(a: EntitySet, b: EntitySet): boolean {
  if (a === b) {
    return true;
  }

  for (const hash in a) {
    if (!b[hash]) {
      return false;
    }
  }

  return true;
}

/**
 * Performs an XOR ("exclusive or") of two entity sets.
 *
 * @param a - The first entity set.
 * @param b - The second entity set.
 * @returns A new entity set that contains all the entities from both sets, excluding duplicates.
 *
 * Use case - When you want to toggle elements (deselect if selected, select if not selected)
 */
function entitySetXOR(a: EntitySet, b: EntitySet): EntitySet {
  if (objectIsEmpty(a)) {
    return b;
  }
  if (objectIsEmpty(b)) {
    return a;
  }

  const set = {};
  for (const hash in a) {
    if (!b[hash]) {
      set[hash] = a[hash];
    }
  }
  for (const hash in b) {
    if (!a[hash]) {
      set[hash] = b[hash];
    }
  }
  return set;
}

/**
 * Performs a union of two entity sets.
 *
 * @param a - The first entity set.
 * @param b - The second entity set.
 * @returns A new entity set that contains all the entities from both sets.
 *
 * Use case - When you want to add new entities to an existing selection
 */
export function entitySetUnion(a: EntitySet, b: EntitySet): EntitySet {
  if (objectIsEmpty(b)) {
    return a;
  }
  if (objectIsEmpty(a)) {
    return b;
  }

  const set = { ...a };
  for (const hash in b) {
    set[hash] = b[hash];
  }
  return set;
}

/**
 * Performs an intersection of two entity sets.
 *
 * @param a - The first entity set.
 * @param b - The second entity set.
 * @returns A new entity set that contains only the entities that exist in both sets.
 *
 * Use case - When you want to find entities that are common between two selections
 */
export function entitySetIntersect(a: EntitySet, b: EntitySet): EntitySet {
  if (objectIsEmpty(a) || objectIsEmpty(b)) {
    return emptyEntitySet;
  }

  const output = {};

  for (const hash in a) {
    if (b[hash]) {
      output[hash] = a[hash];
    }
  }

  return output;
}

/**
 * Performs a difference of two entity sets.
 *
 * @param a - The first entity set.
 * @param b - The second entity set.
 * @returns A new entity set that contains all the entities from the first set that are not in the second set.
 *
 * Use case - When you want to remove entities from an existing selection
 */
export function entitySetDiff(a: EntitySet, b: EntitySet): EntitySet {
  if (objectIsEmpty(b)) {
    return a;
  }
  if (objectIsEmpty(a)) {
    return emptyEntitySet;
  }

  const set = { ...a };
  for (const hash in b) {
    delete set[hash];
  }
  return set;
}

export function entitySetContainsType(set: EntitySet, type: DocEntityType | DocEntityType[]): boolean {
  if (!Array.isArray(type)) {
    for (const hash in set) {
      if (set[hash].type === type) {
        return true;
      }
    }
    return false;
  }

  if (type.length === 1) {
    return entitySetContainsType(set, type[0]);
  }

  const typeLookup = type.length > 3 ? makeEntityTypeLookup(type) : null;
  for (const hash in set) {
    const handleType = set[hash].type;
    if (typeLookup ? typeLookup[handleType] : type.includes(handleType)) {
      return true;
    }
  }
  return false;
}

export function entitySetContains(
  set: EntitySet,
  predicate: DocEntityHandle | ((handle: DocEntityHandle) => boolean),
): boolean {
  if (typeof predicate === "function") {
    for (const hash in set) {
      if (predicate(set[hash])) {
        return true;
      }
    }
    return false;
  }

  return !!set[docEntityHandleHash(predicate)];
}

export function entitySetOnlyContainsType(set: EntitySet, type: DocEntityType | DocEntityType[]): boolean {
  if (objectIsEmpty(set)) {
    return false;
  }

  if (!Array.isArray(type)) {
    for (const hash in set) {
      if (set[hash].type !== type) {
        return false;
      }
    }
    return true;
  }

  if (type.length === 1) {
    return entitySetOnlyContainsType(set, type[0]);
  }

  const typeLookup = type.length > 3 ? makeEntityTypeLookup(type) : null;
  for (const hash in set) {
    const handleType = set[hash].type;
    const matches = typeLookup ? !!typeLookup[handleType] : type.includes(handleType);
    if (!matches) {
      return false;
    }
  }
  return true;
}

export function entitySetAdd(set: EntitySet, handle: DocEntityHandle) {
  return {
    ...set,
    [docEntityHandleHash(handle)]: handle,
  };
}

export function makeEntityTypeLookup(types: DocEntityType[]) {
  const lookup: { [key in DocEntityType]?: boolean } = {};
  for (const type of types) {
    lookup[type] = true;
  }
  return lookup;
}

/**
 * Only evaluates to false if the selection contains a change in any of the types we are interested in.
 */
export function entitySetCompare(
  setA: EntitySet | undefined,
  setB: EntitySet | undefined,
  entityTypes: {
    [key in DocEntityType]?: boolean;
  },
) {
  if (entitySetHasEntityType(setA, entityTypes)) {
    return setA === setB;
  }
  if (!entitySetHasEntityType(setB, entityTypes)) {
    // if neither selection contains any of the types we are interested in, we don't need to trigger this render
    return true;
  }

  // new selection has the types, but old selection doesn't
  return false;
}

export function entitySetHasEntityType(
  selection: EntitySet | undefined,
  entityTypes: {
    [key in DocEntityType]?: boolean;
  },
) {
  if (!selection) {
    return false;
  }
  for (const key in selection) {
    const value = selection[key];
    const type = value?.type;
    if (entityTypes[type]) {
      return true;
    }
  }
  return false;
}

export function isSingleSelectedEntity(handle: DocEntityHandle, selected: EntitySet | undefined): boolean {
  if (selected === undefined || objectIsEmpty(selected)) {
    return false;
  }

  if (objectKeyCount(selected) > 1) {
    return false;
  }

  const hash = docEntityHandleHash(handle);
  return !!selected[hash];
}
